import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  IconButton,
  TextField,
  Button,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteConfirmation from "../../common/DeleteConfirmation";
import { NotificationManager } from "react-notifications";
import { GetEmailAccounts, DeleteEmailAccount } from "../../../services/EmailAccountsService";
import ViewEmailAccount from "./ViewEmailAccount";
import CommonLoader from "../../common/CommonLoader";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddEmailAccount from "./AddEmailAccount";

const EmailAccountsTable = () => {
  const columns = [
    "Account Id",
    "Email Name",
    "Status",
    "Actions",
  ];
  const columnHeaders = [
    "id",
    "name",
    "isActive",
  ];
  const rowsPerPageOptions = [10, 25, 50, 100, 200];

  const [accountsData, setAccountsData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isDeleted, setIsDeleted] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
    useState(false);
  const [isAddAccountDialogOpen, setIsAddAccountDialogOpen] = useState(false);

  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        setLoading(true);
        const response = await GetEmailAccounts();
        setAccountsData(response.results);
        setFilteredData(response.results);
      } catch (error) {
        console.error("Error fetching email accounts:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAccounts();
  }, []);

  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        setLoading(true);
        const response = await GetEmailAccounts();
        setAccountsData(response.results);
        setFilteredData(response.results);
      } catch (error) {
        console.error("Error fetching email accounts:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAccounts();
  }, [isDeleted]);

  useEffect(() => {
    if (!accountsData) {
      return;
    }

    const filteredResults = accountsData.filter((account) =>
      columnHeaders.some((header) => {
        const value = account[header];
        if (header === "isActive") {
          const statusText = value ? "Active" : "Inactive";
          return statusText.toLowerCase().includes(searchTerm.toLowerCase());
        }
        return value && typeof value === "string"
          ? value.toLowerCase().includes(searchTerm.toLowerCase())
          : value && typeof value === "number"
          ? value.toString().includes(searchTerm)
          : false;
      })
    );

    setFilteredData(filteredResults);
  }, [searchTerm, accountsData]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewClick = (account) => {
    setSelectedAccount(account);
  };

  const handleBackClick = () => {
    setSelectedAccount(null);
    setSearchTerm("");
  };

  const handleDeleteClick = (account) => {
    setSelectedAccount(account.id);
    setIsDeleteConfirmationOpen(true);
  };

  const handleDeleteConfirmationClose = () => {
    setIsDeleteConfirmationOpen(false);
    setSelectedAccount(null);
  };

  const handleDeleteConfirmationConfirm = async () => {
    try {
      setLoading(true);
      const response = await DeleteEmailAccount(selectedAccount);
      if (response.results === true) {
        NotificationManager.success("Email account deleted successfully");
        setIsDeleteConfirmationOpen(false);
        setIsDeleted(!isDeleted);
        setSelectedAccount(null);
      } else {
        NotificationManager.error("Error deleting email account");
      }
    } catch (error) {
      NotificationManager.error("Error deleting email account");
    } finally {
      setLoading(false);
    }
  };

  const handleAddAccountClick = () => {
    setSelectedAccount(null);
    setIsAddAccountDialogOpen(true);
  };

  const handleEditAccountClick = (account) => {
    setSelectedAccount(account);
    setIsAddAccountDialogOpen(true);
  };

  const handleAddAccountDialogClose = () => {
    setIsAddAccountDialogOpen(false);
    setSelectedAccount(null);
  };

  const handleAddAccountDialogSuccess = () => {
    setIsAddAccountDialogOpen(false);
    setSelectedAccount(null);
    setIsDeleted(!isDeleted);
  };

  const renderActions = (account) => (
    <div style={{ minWidth: "120px" }}>
      <IconButton
        aria-label="edit"
        style={{ color: "orange" }}
        onClick={() => handleEditAccountClick(account)}
      >
        <EditIcon />
      </IconButton>
      <IconButton
        aria-label="view"
        style={{ color: "green" }}
        onClick={() => handleViewClick(account)}
      >
        <VisibilityIcon />
      </IconButton>
      <IconButton
        aria-label="delete"
        style={{ color: "red" }}
        onClick={() => handleDeleteClick(account)}
      >
        <DeleteIcon />
      </IconButton>
    </div>
  );

  return (
    <>
      <span className="title">Email Accounts</span>
      <hr />
      {selectedAccount && !isAddAccountDialogOpen && !isDeleteConfirmationOpen ? (
        <>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBackClick}
            style={{ marginBottom: "16px", color: "green" }}
          >
            Back
          </Button>
          <ViewEmailAccount account={selectedAccount} />
        </>
      ) : (
        <>
          <div
            style={{
              display: "flex",
              marginBottom: "16px",
              alignItems: "center",
            }}
          >
            <TextField
              label="Search"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ marginRight: "16px", width: "80%" }}
            />
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAddAccountClick}
            >
              New
            </Button>
          </div>
          <Paper style={{ marginRight: "20px" }}>
            <TableContainer>
              <Table sx={{ minWidth: 400 }} size="small">
                <TableHead
                  sx={{
                    backgroundColor: (theme) => theme.palette.primary.main,
                    color: "white",
                  }}
                >
                  <TableRow>
                    {columns.map((column) => (
                      <TableCell key={column}>{column}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredData
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((account, index) => (
                      <TableRow
                        key={account.id}
                        sx={{
                          backgroundColor:
                            index % 2 === 0 ? "rgba(0, 0, 0, 0.04)" : "white",
                        }}
                      >
                        <TableCell>{account.id}</TableCell>
                        <TableCell>{account.name}</TableCell>
                        <TableCell>
                          {account.isActive ? "Active" : "Inactive"}
                        </TableCell>
                        <TableCell>{renderActions(account)}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={rowsPerPageOptions}
              component="div"
              count={filteredData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
          <CommonLoader loading={loading} />
          <DeleteConfirmation
            isOpen={isDeleteConfirmationOpen}
            onClose={handleDeleteConfirmationClose}
            onConfirm={handleDeleteConfirmationConfirm}
            title="Delete Confirmation" 
            message="Are you sure you want to delete this email account?"           
            color="red"
          />
        </>
      )}

      {isAddAccountDialogOpen && (
        <AddEmailAccount
          open={isAddAccountDialogOpen}
          onClose={handleAddAccountDialogClose}
          onSave={handleAddAccountDialogSuccess}
          selectedAccount={selectedAccount}
        />
      )}
    </>
  );
};

export default EmailAccountsTable;